import { IBCR } from "../../../types/IBCR";
import { FileOrFolder } from "../../../types/FileOrFolder";
import api from "../../axiosSetup";
import { filterValidMoveItems, findAllChildren, isDescendant, MAX_COLUMNS, MAX_ROWS } from "../../utils/general";
import { createSlice, PayloadAction, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import toast from "react-hot-toast";
import { getApiClient } from "@/utils/getApiClient";
import { it } from "date-fns/locale";
import { IMeetingMinutes } from "../../../types/IMeetingMinutes";
import type { SearchPageMetadata, SearchPageRequest, SearchPageResponse } from "../../../types/SearchResultsPage";
import { eagerSearchKey, searchPageQuery, selectEagerSearchPage } from "@/utils-2/searchResultsPage";

function sortByResolution(items: FileOrFolder[]) {
  return [...items].sort((a, b) => {
    const ra = a.bcrContent?.resolutionNumber;
    const rb = b.bcrContent?.resolutionNumber;

    const aHas = !!ra;
    const bHas = !!rb;

    if (aHas !== bHas) return aHas ? -1 : 1;
    if (aHas && bHas) return Number(ra) - Number(rb);

    // fallback for non-BCR
    return (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" });
  });
}

function reflowGrid(items: FileOrFolder[], maxCols: number) {
  return items.map((item, i) => ({
    ...item,
    x: i % maxCols,
    y: Math.floor(i / maxCols),
  }));
}

type FileExplorerHistoryEntry = {
  folderId: string | null;
  page: number;
};

// interface FileExplorerState {
//     allItems: FileOrFolder[];
//     visibleItems: FileOrFolder[];
//     loading: boolean;
//     error: string | null;
//     isSaving: boolean;
//     mode: 'search' | 'explorer' | 'meeting-minutes' | string;
//     currentFolderId: string | null;
//     breadcrumb: { _id: string | null; name: string }[] // Track folder history
//     propertiesPath: { _id: string | null; name: string }[] // Track folder history
//     selectedItems: FileOrFolder[], // FileOrFolder[] ?
//     clipboard: FileOrFolder[],
//     clipboardType: "copy" | "cut" | null; // Tracks whether it's copy or cut
//     metadata: string[],
//     dropZonePosition: { x: number; y: number } | null;
//     // NEW
//     history: (string | null)[];
//     historyIndex: number; // points at current entry in history[]
// }
interface FileExplorerState {
  searchPageRequest: SearchPageRequest | null;
  searchPage: SearchPageMetadata | null;
  searchPageCache: Record<string, { items: FileOrFolder[]; pagination: SearchPageMetadata }>;
  searchEagerResult: { key: string; items: FileOrFolder[]; pagination: SearchPageMetadata } | null;
  searchRequestId: string | null;
  allItems: FileOrFolder[];
  visibleItems: FileOrFolder[];
  loading: boolean;
  error: string | null;
  isSaving: boolean;
  mode: "search" | "explorer" | "meeting-minutes" | string;
  currentFolderId: string | null;
  currentPage: number;
  breadcrumb: { _id: string | null; name: string }[];
  propertiesPath: { _id: string | null; name: string }[];
  selectedItems: FileOrFolder[];
  clipboard: FileOrFolder[];
  clipboardType: "copy" | "cut" | null;
  metadata: string[];
  dropZonePosition: { x: number; y: number } | null;
  history: FileExplorerHistoryEntry[];
  historyIndex: number;
}

const initialState: FileExplorerState = {
  searchPageRequest: null,
  searchPage: null,
  searchPageCache: {},
  searchEagerResult: null,
  searchRequestId: null,
  allItems: [],
  visibleItems: [],
  loading: false,
  error: null,
  isSaving: false,
  mode: "explorer",
  currentFolderId: null,
  currentPage: 0,
  breadcrumb: [{ _id: null, name: "Files (Root)" }],
  propertiesPath: [{ _id: null, name: "Files (Root)" }],
  selectedItems: [],
  clipboard: [],
  clipboardType: null,
  metadata: [],
  dropZonePosition: { x: 0, y: 0 },
  history: [{ folderId: null, page: 0 }],
  historyIndex: 0,
};

function forEachStoredSearchItem(
  state: FileExplorerState,
  patchItem: (item: FileOrFolder) => void,
) {
  const collections = [
    state.visibleItems,
    state.allItems,
    ...(state.searchEagerResult ? [state.searchEagerResult.items] : []),
    ...Object.values(state.searchPageCache).map(page => page.items),
  ];

  for (const items of collections) items.forEach(patchItem);
}

const fileExplorerSlice = createSlice({
    name: "fileExplorer",
    initialState,
    reducers: {
        // NEW
        resetExplorerNavigation: (state) => {
          state.currentFolderId = null;
          state.currentPage = 0;
          state.mode = "explorer";

          state.history = [{ folderId: null, page: 0 }];
          state.historyIndex = 0;

          state.breadcrumb = [{ _id: null, name: "Files (Root)" }];
          state.visibleItems = state.allItems.filter((item) => item.parentId === null);

          state.selectedItems = [];
          state.dropZonePosition = { x: 0, y: 0 };
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
          state.currentPage = Math.max(0, action.payload);
        },
        clampCurrentPage: (state, action: PayloadAction<number>) => {
          const totalPages = Math.max(1, action.payload);
          state.currentPage = Math.min(state.currentPage, totalPages - 1);
        },
        goToPage: (state, action: PayloadAction<number>) => {
          const nextPage = Math.max(0, action.payload);

          const folderId = state.currentFolderId;

          const trimmed = state.history.slice(0, state.historyIndex + 1);
          const last = trimmed[trimmed.length - 1];

          if (last?.folderId === folderId && last?.page === nextPage) return;

          trimmed.push({
            folderId,
            page: nextPage,
          });

          state.history = trimmed;
          state.historyIndex = trimmed.length - 1;

          state.currentPage = nextPage;
        },
        goToFolder: (
          state,
          action: PayloadAction<{ folderId: string | null; page?: number } | string | null>
        ) => {
          const rawPayload = action.payload;

          const folderIdRaw =
            typeof rawPayload === "object" && rawPayload !== null
              ? rawPayload.folderId
              : rawPayload;

          const page =
            typeof rawPayload === "object" && rawPayload !== null
              ? rawPayload.page ?? 0
              : 0;

          const folderId =
            folderIdRaw === "" || folderIdRaw === "root" ? null : folderIdRaw;

          const trimmed = state.history.slice(0, state.historyIndex + 1);
          const last = trimmed[trimmed.length - 1];

          if (last?.folderId === folderId && last?.page === page) {
            state.currentFolderId = folderId;
            state.currentPage = page;
            state.mode = "explorer";
            state.visibleItems = state.allItems.filter((i) => i.parentId === folderId);
            return;
          }

          trimmed.push({ folderId, page });

          state.history = trimmed;
          state.historyIndex = trimmed.length - 1;

          state.currentFolderId = folderId;
          state.currentPage = page;
          state.mode = "explorer";

          state.breadcrumb = buildBreadcrumb(folderId, state.allItems);
          state.visibleItems = state.allItems.filter((i) => i.parentId === folderId);
        },
        // NEW
        goBack: (state) => {
          if (state.historyIndex <= 0) return;

          state.historyIndex -= 1;

          const entry = state.history[state.historyIndex] ?? {
            folderId: null,
            page: 0,
          };

          state.currentFolderId = entry.folderId;
          state.currentPage = entry.page;
          state.mode = "explorer";
          state.breadcrumb = buildBreadcrumb(entry.folderId, state.allItems);
          state.visibleItems = state.allItems.filter(
            (i) => i.parentId === entry.folderId
          );
        },
        // NEW
        goForward: (state) => {
          if (state.historyIndex >= state.history.length - 1) return;

          state.historyIndex += 1;

          const entry = state.history[state.historyIndex] ?? {
            folderId: null,
            page: 0,
          };

          state.currentFolderId = entry.folderId;
          state.currentPage = entry.page;
          state.mode = "explorer";
          state.breadcrumb = buildBreadcrumb(entry.folderId, state.allItems);
          state.visibleItems = state.allItems.filter(
            (i) => i.parentId === entry.folderId
          );
        },
        setSaving: (state, action: PayloadAction<boolean>) => {
            state.isSaving = action.payload;
        },
        getVisibleItems: (state, action: PayloadAction<FileOrFolder[]>) => {
            state.visibleItems = action.payload.filter(item => item.parentId === state.currentFolderId);
        },
        // Meant for searches in /file-explorer
        setVisibleItemsMeetingMinutes: (state, action: PayloadAction<FileOrFolder[]>) => {
            state.loading = false;
            state.visibleItems = action.payload;
            state.mode = 'meeting-minutes';
        },
        //Dragging pos
        updateItemPositions: (state, action: PayloadAction<{ 
            items: FileOrFolder[]; 
            startX: number; 
            startY: number; 
            draggedItemId: string;
        }>) => {
            const { items, startX, startY, draggedItemId } = action.payload;
            const draggedItem = state.allItems.find(item => item._id === draggedItemId);
            if (!draggedItem) return;
        
            const offsetX = startX - draggedItem.x;
            const offsetY = startY - draggedItem.y;
        
            // ✅ Move items in `allItems`
            state.allItems.forEach(item => {
                if (items.some(movedItem => movedItem._id === item._id)) {
                    item.x += offsetX;
                    item.y += offsetY;
                    item.parentId = state.currentFolderId;
                }
            });
        
            // ✅ Move items in `visibleItems`
            state.visibleItems.forEach(item => {
                if (items.some(movedItem => movedItem._id === item._id)) {
                    item.x += offsetX;
                    item.y += offsetY;
                    item.parentId = state.currentFolderId;
                }
            });
        
            // ✅ Ensure moved items are in `visibleItems`
            items.forEach(movedItem => {
                const isAlreadyVisible = state.visibleItems.some(item => item._id === movedItem._id);
                if (!isAlreadyVisible) {
                    const newItem = state.allItems.find(item => item._id === movedItem._id);
                    if (newItem) {
                        state.visibleItems.push({ ...newItem });
                    }
                }
            });
        
            // ✅ Ensure dragged item is visible
            const isDraggedItemVisible = state.visibleItems.some(item => item._id === draggedItemId);
            if (!isDraggedItemVisible) {
                const newDraggedItem = state.allItems.find(item => item._id === draggedItemId);
                if (newDraggedItem) {
                    state.visibleItems.push({ ...newDraggedItem });
                }
            }
        },
            //Dragging pos
            resolveOverlapsInState: (state) => {
                const updatedVisibleItems = resolveOverlaps(state.visibleItems);
    
                // Update visible items in the state
                state.visibleItems = updatedVisibleItems;
            
                // Update allItems to reflect the new positions
                state.allItems = state.allItems.map(item => {
                    // Check if the item exists in visibleItems (i.e., has moved)
                    const updatedItem = updatedVisibleItems.find(visible => visible._id === item._id);
                    return updatedItem ? { ...item, x: updatedItem.x, y: updatedItem.y } : item;
                });    
            },
            updateItemDirectory: (state, action: PayloadAction<{ 
                items: FileOrFolder[];
                newParentId: string | null;
                x?: number;
                y?: number;
            }>) => {
                const { items, newParentId, x, y } = action.payload;
            
                // ✅ Get all existing items in the new parent folder
                const existingItems = state.allItems.filter(item => item.parentId === newParentId);
            
                // ✅ Calculate new positions
                const updatedItems = resolveNewPositions(items, existingItems, newParentId, x, y);
            
                // ✅ Apply new positions to `allItems`
                updatedItems.forEach(updatedItem => {
                    const existingItemIndex = state.allItems.findIndex(i => i._id === updatedItem._id);
                    if (existingItemIndex !== -1) {
                        state.allItems[existingItemIndex] = { ...state.allItems[existingItemIndex], ...updatedItem };
                    }
                });
            
                // ✅ Recompute `visibleItems` based on the updated `allItems`
                state.visibleItems = state.allItems.filter(item => item.parentId === state.currentFolderId);
            },
            updateItemsAfterPaste: (state, action: PayloadAction<{ mergedItems: FileOrFolder[], newParentId: string | null }>) => {
                const { mergedItems, newParentId } = action.payload;
            
                // ✅ Remove old versions of the updated items from `allItems`
                state.allItems = [
                    ...state.allItems.filter(item => !mergedItems.some(m => m._id === item._id)),
                    ...mergedItems
                ];
            
                // ✅ If the pasted items belong to the current folder, filter them properly
                if (state.currentFolderId === newParentId) {
                    state.visibleItems = [
                        ...state.visibleItems.filter(item => !mergedItems.some(m => m._id === item._id)), // Remove old versions
                        ...mergedItems.filter(item => item.parentId === state.currentFolderId) // ✅ Only add items matching the current folder
                    ];
                }
            },
        setMode: (state, action: PayloadAction<string>) => {
            state.mode = action.payload;
        },
        setCurrentFolderId: (state, action: PayloadAction<string | null>) => {
            //Fetch visibleItems
            state.visibleItems = state.allItems.filter(item => item.parentId === action.payload);

            state.currentFolderId = action.payload;

            state.mode = 'explorer';

            if (action.payload == null) {
                state.breadcrumb = [{ _id: null, name: "Files (Root)" }];
            } else {
                const folder = state.allItems.find((item) => item._id === action.payload);
                if (folder) {
                    state.breadcrumb.push({ _id: folder._id, name: folder.name });
                }
            }
        },
        setCurrentFolderNull: (state, action) => {
            state.currentFolderId = null;
        },
        setCurrentFolderId2: (state, action: PayloadAction<{ folderId: string | null }>) => {
            state.currentFolderId = action.payload.folderId;
        
            const breadcrumb: { _id: string | null; name: string }[] = [];
        
            state.mode = 'explorer';

            if (!action.payload.folderId) {
                state.breadcrumb = [{ _id: null, name: "Files (Root)" }];
                return;
            }
        
            // ✅ Use the recursive function to find the folder
            let currentFolder = findFolderById(action.payload.folderId, state.allItems);
        
            while (currentFolder) {
                breadcrumb.unshift({ _id: currentFolder._id, name: currentFolder.name }); // ✅ Insert at the start
                currentFolder = findFolderById(currentFolder.parentId ?? "", state.allItems);
            }
        
            // ✅ Ensure "Files (Root)" is always at the beginning
            breadcrumb.unshift({ _id: null, name: "Files (Root)" });
        
            state.breadcrumb = breadcrumb;
        },
        // Navigate to folder and trim breadcrumb trail
        navigateToFolder: (state, action: PayloadAction<string | null>) => {
            const getPath = action.payload === '' ? null : action.payload === 'root' ? null : action.payload;
            const index = state.breadcrumb.findIndex((folder) => folder._id === action.payload);
            console.log("Breadcrumb", getPath)
            if (index !== -1) {
                state.breadcrumb = state.breadcrumb.slice(0, index + 1);
                state.currentFolderId = getPath;
            }
        },
        setPropertiesPath: (state, action: PayloadAction<{ _id: string; name: string }[]>) => {
            state.propertiesPath = action.payload;
        },
        navigateToFolderProperties: (state, action: PayloadAction<string | null>) => {
            state.mode = 'explorer';
            const getPath = action.payload === '' ? null : action.payload;
            state.currentFolderId = getPath;
            const index = state.propertiesPath.findIndex((folder) => folder._id === action.payload);
            if (index !== -1) {
                state.breadcrumb = state.propertiesPath.slice(0, index + 1);
            }
        },
        // Add, Remove, Update Items
        addItem: (state, action: PayloadAction<FileOrFolder>) => {
            const newItem = action.payload;
        
            // ✅ Always add to `allItems`
            state.allItems.push(newItem);
        
            // ✅ Only add to `visibleItems` if it belongs in the current folder
            if (String(newItem.parentId) === String(state.currentFolderId)) {
                state.visibleItems.push(newItem);
            }
        },
        // ✅ Delete a folder locally
        deleteItem: (state, action: PayloadAction<string | null>) => {
            const itemId = action.payload;
            if (!itemId) return;
        
            const childIds = findAllChildren(state.allItems, itemId);
        
            const idsToRemove = [itemId, ...childIds];
        
            state.allItems = state.allItems.filter(item => item._id && !idsToRemove.includes(item._id));
            state.visibleItems = state.visibleItems.filter(item => item._id && !idsToRemove.includes(item._id));
        },
        // ✅ Update a folder locally
        updateItem: (state, action: PayloadAction<FileOrFolder>) => {
            const index = state.allItems.findIndex(item => item._id === action.payload._id);
            if (index !== -1) {
                state.allItems[index] = { ...state.allItems[index], ...action.payload };
            }
            state.visibleItems = state.allItems.filter(item => item.parentId === state.currentFolderId);
        },
        updateItemSearched: (state, action: PayloadAction<FileOrFolder>) => {
            const index = state.allItems.findIndex(item => item._id === action.payload._id);
            if (index !== -1) {
                const existingItem = state.allItems[index];
                state.allItems[index] = {
                    ...existingItem,
                    ...action.payload,
                    x: existingItem.x, // preserve x
                    y: existingItem.y, // preserve y
                };
            }
            // Update visibleItems with the same x/y preservation
            state.visibleItems = state.allItems
                .filter(item => item.parentId === state.currentFolderId)
                .map(item => {
                    if (item._id === action.payload._id) {
                        return {
                            ...item,
                            ...action.payload,
                            x: item.x,
                            y: item.y,
                        };
                    }
                    return item;
                });
        },
        // Select Items reducers
        setSelectedItems: (state, action: PayloadAction<FileOrFolder[]>) => {
            state.selectedItems = action.payload;
        },
        toggleSelectedItem: (state, action: PayloadAction<FileOrFolder>) => {
            if (state.selectedItems.includes(action.payload)) {
                state.selectedItems = state.selectedItems.filter(id => id !== action.payload);
            } else {
                state.selectedItems.push(action.payload);
            }
        },
        addSelectedItem: (state, action: PayloadAction<FileOrFolder>) => {
            // Add item only if it's not already selected
            if (!state.selectedItems.some(item => item._id === action.payload._id)) {
                state.selectedItems.push(action.payload);
            }
        },
        removeSelectedItem: (state, action: PayloadAction<{ _id: string | null }>) => {
            // Remove selected item
            state.selectedItems = state.selectedItems.filter(item => item._id !== action.payload._id);
        },
        clearSelectedItems: (state) => {
            state.selectedItems = [];
        },
        // Clipboard stuff
        copyItems: (state, action: PayloadAction<FileOrFolder[]>) => {
            state.clipboard = action.payload;
            state.clipboardType = "copy";
        },
        cutItems: (state, action: PayloadAction<FileOrFolder[]>) => {
            state.clipboard = action.payload;
            state.clipboardType = "cut";
        },
        clearClipboard: (state) => {
            state.clipboard = [];
            state.clipboardType = null;
        },
        setDropZonePos: (state, action: PayloadAction<{ x: number; y: number}>) => {
            state.dropZonePosition = action.payload;
        },
        clearDropZonePos: (state) => {
            state.dropZonePosition = { x: 0, y: 0 };
        },
        renameFolder: (state, action: PayloadAction<{ _id: string | null; newName: string }>) => {
            const { _id, newName } = action.payload;

            // ✅ Update `allItems`
            const folderIndex = state.allItems.findIndex(folder => folder._id === _id);
            if (folderIndex !== -1) {
                state.allItems[folderIndex].name = newName;
            }

            // ✅ Update `visibleItems`
            const visibleIndex = state.visibleItems.findIndex(folder => folder._id === _id);
            if (visibleIndex !== -1) {
                state.visibleItems[visibleIndex].name = newName;
            }
        },
        // NEW
        updateSupportingDocBCRName: (
            state,
            action: PayloadAction<{ bcrId: string; docId: string; newName: string }>
            ) => {
            const { bcrId, docId, newName } = action.payload;

            const collections = [
                state.visibleItems,
                state.allItems,
                ...(state.searchEagerResult ? [state.searchEagerResult.items] : []),
                ...Object.values(state.searchPageCache).map(page => page.items),
            ];

            for (const items of collections) {
                for (const item of items) {
                    if (String(item._id) === String(docId)) item.name = newName;
                    if (String(item.bcrContent?._id) !== String(bcrId)) continue;

                    const doc = item.bcrContent?.supportingDocs?.find(
                        candidate => String(candidate._id) === String(docId)
                    );
                    if (doc) doc.name = newName;
                }
            }
        },
        // NEW
        updateSupportingDocMMName: (
            state,
            action: PayloadAction<{ mmId: string; docId: string; newName: string }>
            ) => {
            const { mmId, docId, newName } = action.payload;

            const collections = [
                state.visibleItems,
                state.allItems,
                ...(state.searchEagerResult ? [state.searchEagerResult.items] : []),
                ...Object.values(state.searchPageCache).map(page => page.items),
            ];

            for (const items of collections) {
                for (const item of items) {
                    if (String(item._id) === String(docId)) item.name = newName;
                    if (String(item.meetingMinuteContent?._id) !== String(mmId)) continue;

                    const doc = item.meetingMinuteContent?.supportingDocs?.find(
                        candidate => String(candidate._id) === String(docId)
                    );
                    if (doc) doc.name = newName;
                }
            }
        },
        // NEW
        updateVisibleItemName: (state, action: PayloadAction<{ itemId: string, newName: string }>) => {
            const collections = [
                state.visibleItems,
                state.allItems,
                ...(state.searchEagerResult ? [state.searchEagerResult.items] : []),
                ...Object.values(state.searchPageCache).map(page => page.items),
            ];

            for (const items of collections) {
                const item = items.find(
                    candidate => String(candidate._id) === String(action.payload.itemId)
                );
                if (item) item.name = action.payload.newName;
            }
        },
        //Works for BCR, but docs?
        addItemDashboard: (state, action: PayloadAction<FileOrFolder>) => {
            const newItem = action.payload;
            const allItemsIndex = state.allItems.findIndex(item => String(item._id) === String(newItem._id));
            if (allItemsIndex === -1) state.allItems.push(newItem);
            else state.allItems[allItemsIndex] = newItem;

            const visibleItemsIndex = state.visibleItems.findIndex(item => String(item._id) === String(newItem._id));
            if (visibleItemsIndex === -1) state.visibleItems.push(newItem);
            else state.visibleItems[visibleItemsIndex] = newItem;
        },
        //NEW
        setSignedBCRVisibleItems(state, action: PayloadAction<{ signedFileId: string, bcrId: string }>) {
            const { signedFileId, bcrId } = action.payload;
            // console.log('bcrFileId', bcrFileId)

            const bcrFile = state.visibleItems.find(item => item.bcrContent?._id === bcrId);
            if (!bcrFile?.bcrContent?.supportingDocs) return;

            // const before = bcrFile.bcrContent.supportingDocs;
            // console.log("Before:", JSON.stringify(before, null, 2));
            // bcrFile.bcrContent.supportingDocs = before.filter(doc => doc._id !== signedFileId);
            // console.log("After:", JSON.stringify(bcrFile.bcrContent.supportingDocs, null, 2));
            const supportingDocs = bcrFile.bcrContent.supportingDocs;

            // for (const doc of supportingDocs) {
            //     doc.attachSignedBCR = null;
            // }

            console.log('2');
            
            // Find the signed file
            const signedFile = bcrFile.bcrContent.supportingDocs.find(doc => doc._id === signedFileId);
            
            console.log('3');
            
            if (!signedFile) return;
            
            console.log('4');

            // signedFile.attachSignedBCR = bcrFile?.bcrContent._id;
            // bcrFile.bcrContent.fullySignedDocument = signedFile;
        },
        removeSignedBCRVisibleItems(state, action: PayloadAction<{ signedFileId: string, bcrId: string }>) {
            const { signedFileId, bcrId } = action.payload;

            const bcrFile = state.visibleItems.find(item => item.bcrContent?._id === bcrId);
            if (!bcrFile?.bcrContent?.supportingDocs) return;

            const supportingDocs = bcrFile.bcrContent.supportingDocs;

            const signedFile = supportingDocs.find(doc => doc._id === signedFileId);

            if (!signedFile) return;
           
            // signedFile.attachSignedBCR = null;
            // bcrFile.bcrContent.fullySignedDocument = null;
        },
        // Both BCR files AND Supporting Docs
        removeFileFromVisibleItems: (
          state,
          action: PayloadAction<string> // Accepts just a string
        ) => {
          const docId = action.payload;

          const removeDocument = (items: FileOrFolder[]): FileOrFolder[] =>
            items
              .filter(item => String(item._id) !== String(docId))
              .map(item => ({
                ...item,
                ...(item.bcrContent
                  ? {
                      bcrContent: {
                        ...item.bcrContent,
                        supportingDocs: item.bcrContent.supportingDocs?.filter(
                          doc => String(doc._id) !== String(docId)
                        ),
                      },
                    }
                  : {}),
                ...(item.meetingMinuteContent
                  ? {
                      meetingMinuteContent: {
                        ...item.meetingMinuteContent,
                        supportingDocs: item.meetingMinuteContent.supportingDocs?.filter(
                          doc => String(doc._id) !== String(docId)
                        ),
                      },
                    }
                  : {}),
              }));

          state.visibleItems = removeDocument(state.visibleItems);
          state.allItems = removeDocument(state.allItems);

          if (state.searchEagerResult) {
            state.searchEagerResult.items = removeDocument(
              state.searchEagerResult.items
            );
          }

          for (const page of Object.values(state.searchPageCache)) {
            page.items = removeDocument(page.items);
          }
        },
        // Both BCR files AND Supporting Docs
        addFileToVisibleItems: (
          state,
          action: PayloadAction<FileOrFolder> // Accepts just a string
        ) => {
          const file = action.payload;
          state.visibleItems.push(file);
        },
        updateBCRDashboard: (state, action: PayloadAction<{ file: FileOrFolder; signaturesMergedImg?: string }>) => {
            const { file, signaturesMergedImg } = action.payload;

            if (file?.bcrContent && signaturesMergedImg) {
                file.bcrContent.signaturesMergedImg = signaturesMergedImg;
            }

            const index = state.visibleItems.findIndex(f => f._id === file._id);
            if (index !== -1) {
                state.visibleItems[index] = file;
            }
        },
        // Clear visible Items (Search docs)
        clearItemsDashboard: (state) => {
            state.visibleItems = [];
            state.searchPageRequest = null;
            state.searchPage = null;
            state.searchPageCache = {};
            state.searchEagerResult = null;
            state.searchRequestId = null;
            state.loading = false;
            state.error = null;
        },
        invalidateSearchResultsCache: (state) => {
            // Keep the currently rendered page in place while the mutation owner
            // silently reloads it. Other pages and tabs must be fetched again so
            // they cannot restore records captured before the mutation.
            state.searchPageCache = {};
            state.searchEagerResult = null;
        },
        showCachedSearchPage: (state, action: PayloadAction<SearchPageRequest>) => {
            const request = { ...action.payload, prefetch: false };
            if (state.searchEagerResult?.key === eagerSearchKey(request)) {
              const result = selectEagerSearchPage(
                state.searchEagerResult.items,
                request,
                state.searchEagerResult.pagination,
              );
              state.visibleItems = result.items;
              state.searchPage = result.pagination;
              state.searchPageRequest = request;
              state.searchRequestId = null;
              state.loading = false;
              state.error = null;
              state.mode = 'search';
              return;
            }
            const cached = state.searchPageCache[searchPageQuery(request)];
            if (!cached) return;
            state.visibleItems = cached.items;
            state.searchPage = cached.pagination;
            state.searchPageRequest = request;
            state.searchRequestId = null;
            state.loading = false;
            state.error = null;
            state.mode = 'search';
        },
        //This will add a new supporting document to the BCR content. Use incase of uploading files in /search
        // Adds ONE supporting doc
        updateBCRSupportingDocsDashboard: (state, action: PayloadAction<{ fileId: string; newDoc: FileOrFolder }>) => {
          const { fileId, newDoc } = action.payload;

          const targetItem = state.visibleItems.find(item => item._id === fileId);

          if (targetItem && targetItem.bcrContent) {
            if (!Array.isArray(targetItem.bcrContent.supportingDocs)) {
              targetItem.bcrContent.supportingDocs = [];
            }

            targetItem.bcrContent.supportingDocs.push(newDoc);
          }
        },
        //Replaces ALL supporting docs
        replaceBCRSupportingDocsDashboard: (state, action: PayloadAction<{ fileId: string; newDocs: FileOrFolder[] }>) => {
          const { fileId, newDocs } = action.payload;
          const targetItem = state.visibleItems.find(item => item._id === fileId);
          if (targetItem?.bcrContent) {
            targetItem.bcrContent.supportingDocs = newDocs;
          }
        },
        updateDSImageAndParticipantSignatureStatus: (
          state,
          action: PayloadAction<{ fileId: string; userId: string; signatureDate: Date; signaturesMergedImg: string }>
        ) => {
          const { fileId, userId, signatureDate, signaturesMergedImg } = action.payload;

          const item = state.visibleItems.find(item => item._id === fileId);

          if (item?.bcrContent) {
            item.bcrContent.signaturesMergedImg = signaturesMergedImg;
          }

          if (item?.bcrContent?.participantUsers) {
            const participant = item.bcrContent.participantUsers.find(
              (p) => p.user && typeof p.user === 'object' && p.user._id === userId
            );

            if (participant) {
              participant.isSigned = true;
              participant.signatureDate = signatureDate;
              participant.signatureType = 'Manual';
            }
          }
        },
        // Meant for when deleting a signed Manual signature
        nullifyParticipantSignatureStatusAndDSImage: (
          state,
          action: PayloadAction<{ bcrId: string; userId: string; signaturesMergedImg: string }>
        ) => {
          const { bcrId, userId, signaturesMergedImg } = action.payload;

          const item = state.visibleItems.find(item => item.bcrContent?._id === bcrId.toString());

          if (item?.bcrContent) {
            item.bcrContent.signaturesMergedImg = signaturesMergedImg;
          }

          if (item?.bcrContent?.participantUsers) {
            const participant = item.bcrContent.participantUsers.find(
              (p) => p.user && typeof p.user === 'object' && p.user._id === userId
            );

            if (participant) {
              participant.isSigned = false;
              participant.signatureDate = null;
              participant.signatureType = null;
            }
          }
        },
        updateParticipantExtractedSignature: (
        state,
        action: PayloadAction<{
          bcrFileId: string;
          supportingDocId: string; // croppingFileId
          userId: string;
          extractedSignatureFile: FileOrFolder;
          signaturesMergedImg: string;
        }>
      ) => {
            const { bcrFileId, supportingDocId, userId, extractedSignatureFile, signaturesMergedImg } = action.payload;

            const item = state.visibleItems.find(item => item._id === bcrFileId);

            if (item?.bcrContent) {
                item.bcrContent.signaturesMergedImg = signaturesMergedImg;
            }

            if (item?.bcrContent?.participantUsers) {
            const participant = item.bcrContent.participantUsers.find(
                p => p.user && typeof p.user === 'object' && p.user._id === userId
            );

            if (participant) {
                participant.extractedSignatureFile = extractedSignatureFile;
            }

           // ✅ Replace the matching supportingDoc with extractedSignatureFile
            if (item?.bcrContent?.supportingDocs) {
                const index = item.bcrContent.supportingDocs.findIndex(
                doc => doc._id === supportingDocId
                // doc => doc._id === extractedSignatureFile._id
                );

                console.log('index', index);

                if (index !== -1) {
                    item.bcrContent.supportingDocs[index] = extractedSignatureFile;
                }
            }
        }
      },
      updateExtractedSignatureImageInSupportingDoc: (
        state,
        action: PayloadAction<{
          fileId: string; // The BCR file's _id (from selectedItem or visibleItems)
          supportingDocId: string; // The uploaded doc's _id (croppingFileId)
          signatureUrl: string;
        }>
      ) => {
        const { fileId, supportingDocId, signatureUrl } = action.payload;

        const item = state.visibleItems.find(item => item._id === fileId);
        if (!item?.bcrContent?.supportingDocs) return;

        const targetDoc = item.bcrContent.supportingDocs.find(doc => doc._id === supportingDocId);
        if (!targetDoc) return;

        // if (!targetDoc.singleUploadedBCRSignature) {
        //   targetDoc.singleUploadedBCRSignature = {
        //     userSigned: null,
        //     userName: null,
        //     extractedSignatureImg: null
        //   };
        // }
        // targetDoc.singleUploadedBCRSignature.extractedSignatureImg = signatureUrl;
      },
      setBCRMMFile_FileExplorer: (
        state,
        action: PayloadAction<
          FileOrFolder | undefined | null
        >
      ) => {
        const incoming = action.payload;

        if (!incoming?._id) {
          console.warn(
            "setBCRMMFile_FileExplorer called without a valid file:",
            incoming
          );
          return;
        }

        const preserveXY =
          state.mode === "search" ||
          state.mode === "meeting-minutes";

        const getReferenceId = (
          value: unknown
        ): string | null => {
          if (!value) return null;

          if (typeof value === "string") {
            return value;
          }

          if (
            typeof value === "object" &&
            "_id" in value
          ) {
            const id = (
              value as { _id?: unknown }
            )._id;

            return id ? String(id) : null;
          }

          return null;
        };

        /*
        * Keep these references lightweight.
        * Do not put the complete parent record inside
        * each supporting document.
        */
        const bcrReference = incoming.bcrContent
          ? {
              _id: incoming.bcrContent._id,
              title: incoming.bcrContent.title,
              resolutionNumber:
                incoming.bcrContent.resolutionNumber,
              departmentName:
                incoming.bcrContent.departmentName,
              signingType:
                incoming.bcrContent.signingType,
              recordStatus:
                incoming.bcrContent.recordStatus,
            }
          : null;

        const meetingMinuteReference =
          incoming.meetingMinuteContent
            ? {
                _id:
                  incoming.meetingMinuteContent._id,
                name:
                  incoming.meetingMinuteContent.name,
                uniqueId:
                  incoming.meetingMinuteContent.uniqueId,
                meetingDate:
                  incoming.meetingMinuteContent.meetingDate,
                recordStatus:
                  incoming.meetingMinuteContent.recordStatus,
              }
            : null;

        /*
        * Returns a new supporting-document object.
        * It never mutates the supplied document.
        */
        const patchSupportingDocument = (
          doc: FileOrFolder
        ): FileOrFolder => {
          let nextDoc = doc;

          if (bcrReference) {
            const attachedBcrId = getReferenceId(
              doc.attachDocBCR
            );

            if (
              attachedBcrId ===
              String(bcrReference._id)
            ) {
              nextDoc = {
                ...nextDoc,
                attachDocBCR: {
                  ...bcrReference,
                } as any,
              };
            }
          }

          if (meetingMinuteReference) {
            const attachedMeetingMinuteId =
              getReferenceId(
                doc.attachDocMeetingMinute
              );

            if (
              attachedMeetingMinuteId ===
              String(meetingMinuteReference._id)
            ) {
              nextDoc = {
                ...nextDoc,
                attachDocMeetingMinute: {
                  ...meetingMinuteReference,
                } as any,
              };
            }
          }

          return nextDoc;
        };

        /*
        * Returns a new file object with updated:
        *
        * 1. Top-level supporting-document references
        * 2. Nested BCR supporting documents
        * 3. Nested Meeting Minute supporting documents
        */
        const patchRelatedDocuments = (
          current: FileOrFolder
        ): FileOrFolder => {
          let nextItem = current;

          /*
          * Top-level BCR supporting document.
          */
          if (bcrReference) {
            const attachedBcrId = getReferenceId(
              nextItem.attachDocBCR
            );

            if (
              attachedBcrId ===
              String(bcrReference._id)
            ) {
              nextItem = {
                ...nextItem,
                attachDocBCR: {
                  ...bcrReference,
                } as any,
              };
            }
          }

          /*
          * Top-level Meeting Minute supporting document.
          */
          if (meetingMinuteReference) {
            const attachedMeetingMinuteId =
              getReferenceId(
                nextItem.attachDocMeetingMinute
              );

            if (
              attachedMeetingMinuteId ===
              String(meetingMinuteReference._id)
            ) {
              nextItem = {
                ...nextItem,
                attachDocMeetingMinute: {
                  ...meetingMinuteReference,
                } as any,
              };
            }
          }

          /*
          * Supporting documents nested inside the BCR.
          */
          const currentBcrContent =
            nextItem.bcrContent;

          if (
            bcrReference &&
            currentBcrContent &&
            String(currentBcrContent._id) ===
              String(bcrReference._id)
          ) {
            const existingDocs =
              currentBcrContent.supportingDocs;

            const updatedDocs = Array.isArray(
              existingDocs
            )
              ? existingDocs.map((doc) => ({
                  ...doc,
                  attachDocBCR: {
                    ...bcrReference,
                  } as any,
                }))
              : existingDocs;

            nextItem = {
              ...nextItem,
              bcrContent: {
                ...currentBcrContent,
                supportingDocs: updatedDocs,
              },
            };
          }

          /*
          * Supporting documents nested inside
          * the Meeting Minute.
          */
          const currentMeetingMinute =
            nextItem.meetingMinuteContent;

          if (
            meetingMinuteReference &&
            currentMeetingMinute &&
            String(currentMeetingMinute._id) ===
              String(meetingMinuteReference._id)
          ) {
            const existingDocs =
              currentMeetingMinute.supportingDocs;

            const updatedDocs = Array.isArray(
              existingDocs
            )
              ? existingDocs.map((doc) => ({
                  ...doc,
                  attachDocMeetingMinute: {
                    ...meetingMinuteReference,
                  } as any,
                }))
              : existingDocs;

            nextItem = {
              ...nextItem,
              meetingMinuteContent: {
                ...currentMeetingMinute,
                supportingDocs: updatedDocs,
              },
            };
          }

          return nextItem;
        };

        const updateArray = (
          items: FileOrFolder[]
        ): FileOrFolder[] => {
          return items.map((currentItem) => {
            const isIncomingParent =
              String(currentItem._id) ===
              String(incoming._id);

            /*
            * Clone the incoming parent. The spread is
            * important because the action payload itself
            * may already be frozen.
            */
            const baseItem: FileOrFolder =
              isIncomingParent
                ? preserveXY
                  ? {
                      ...currentItem,
                      ...incoming,
                      x: currentItem.x,
                      y: currentItem.y,
                    }
                  : {
                      ...incoming,
                    }
                : currentItem;

            return patchRelatedDocuments(baseItem);
          });
        };

        state.visibleItems = updateArray(
          state.visibleItems
        );

        state.allItems = updateArray(
          state.allItems
        );

        if (state.searchEagerResult) {
          state.searchEagerResult.items = updateArray(
            state.searchEagerResult.items
          );
        }

        for (const page of Object.values(state.searchPageCache)) {
          page.items = updateArray(page.items);
        }
      },
      // setBCRMMFile_FileExplorer: (state, action: PayloadAction<FileOrFolder | undefined | null>) => {
      //   const incoming = action.payload;

      //   if (!incoming?._id) {
      //     console.warn("setBCRMMFile_FileExplorer called without a valid file:", incoming);
      //     return;
      //   }

      //   const preserveXY =
      //     state.mode === "search" || state.mode === "meeting-minutes";

      //   const index = state.visibleItems.findIndex(item => item._id === incoming._id);

      //   if (index !== -1) {
      //     const existing = state.visibleItems[index];
      //     state.visibleItems[index] = preserveXY
      //       ? {
      //           ...existing,
      //           ...incoming,
      //           x: existing.x,
      //           y: existing.y,
      //         }
      //       : incoming;
      //   }

      //   const allItemsIndex = state.allItems.findIndex(item => item._id === incoming._id);

      //   if (allItemsIndex !== -1) {
      //     const existing = state.allItems[allItemsIndex];
      //     state.allItems[allItemsIndex] = preserveXY
      //       ? {
      //           ...existing,
      //           ...incoming,
      //           x: existing.x,
      //           y: existing.y,
      //         }
      //       : incoming;
      //   }
      // },
      // MEETING MINUTE STUFF /////////////////////////////////////////////////////
      // Quick things - removes meetingMinute from file.bcrContent
        clearMeetingMinuteFromBCRByBcrId: (
          state,
          action: PayloadAction<{ bcrId: string }>
        ) => {
          const { bcrId } = action.payload;

          const patchItem = (item: FileOrFolder) => {
            if (item.bcrContent?._id?.toString() === bcrId) {
              item.bcrContent.meetingMinute = null;
            }
          };

          forEachStoredSearchItem(state, patchItem);
        },
        // Quick things (again) - ADDS bcrContent to file.meetingMinuteContent.bcrLinks
        appendBcrLinkToMeetingMinuteByMeetingMinuteId: (
          state,
          action: PayloadAction<{
            meetingMinuteId: string;
            bcrFile: FileOrFolder;
          }>
        ) => {
          const { meetingMinuteId, bcrFile } = action.payload;

          const bcrId = bcrFile.bcrContent?._id?.toString();
          if (!bcrId || !bcrFile.bcrContent) return;

          const patchItem = (item: FileOrFolder) => {
            if (item.meetingMinuteContent?._id?.toString() !== meetingMinuteId) return;

            if (!Array.isArray(item.meetingMinuteContent.linkedBcrs)) {
              item.meetingMinuteContent.linkedBcrs = [];
            }

            const alreadyExists = item.meetingMinuteContent.linkedBcrs.some(
              (linked) => linked?._id?.toString() === bcrId
            );

            if (!alreadyExists) {
              item.meetingMinuteContent.linkedBcrs.push(bcrFile.bcrContent!);
            }

            // ✅ ADD THIS
            if (!Array.isArray(item.meetingMinuteContent.linkedBcrIds)) {
              item.meetingMinuteContent.linkedBcrIds = [];
            }

            const alreadyHasId = item.meetingMinuteContent.linkedBcrIds.some(
              (id) => String(id) === String(bcrId)
            );

            if (!alreadyHasId) {
              item.meetingMinuteContent.linkedBcrIds.push(bcrId);
            }
          };

          forEachStoredSearchItem(state, patchItem);
        },
        // appendBcrLinkToMeetingMinuteByMeetingMinuteId: (
        //   state,
        //   action: PayloadAction<{
        //     meetingMinuteId: string;
        //     bcrFile: FileOrFolder;
        //   }>
        // ) => {
        //   const { meetingMinuteId, bcrFile } = action.payload;

        //   const bcrId = bcrFile.bcrContent?._id?.toString();
        //   if (!bcrId || !bcrFile.bcrContent) return;

        //   const patchItem = (item: FileOrFolder) => {
        //     if (item.meetingMinuteContent?._id?.toString() !== meetingMinuteId) return;

        //     if (!Array.isArray(item.meetingMinuteContent.linkedBcrs)) {
        //       item.meetingMinuteContent.linkedBcrs = [];
        //     }

        //     const alreadyExists = item.meetingMinuteContent.linkedBcrs.some(
        //       (linked) => linked?._id?.toString() === bcrId
        //     );

        //     if (!alreadyExists) {
        //       item.meetingMinuteContent.linkedBcrs.push(bcrFile.bcrContent!);
        //     }
        //   };

        //   state.visibleItems.forEach(patchItem);
        //   state.allItems.forEach(patchItem);
        // },
        removeLinkedBcrFromMeetingMinuteByMeetingMinuteId: (
          state,
          action: PayloadAction<{
            meetingMinuteId: string;
            bcrId: string;
          }>
        ) => {
          const { meetingMinuteId, bcrId } = action.payload;

          const patchItem = (item: FileOrFolder) => {
            if (item.meetingMinuteContent?._id?.toString() !== meetingMinuteId) return;

            if (!Array.isArray(item.meetingMinuteContent.linkedBcrs)) {
              item.meetingMinuteContent.linkedBcrs = [];
            }

            item.meetingMinuteContent.linkedBcrs =
              item.meetingMinuteContent.linkedBcrs.filter(
                (linked) => linked?._id?.toString() !== bcrId
              );

            // ✅ ADD THIS
            if (Array.isArray(item.meetingMinuteContent.linkedBcrIds)) {
              item.meetingMinuteContent.linkedBcrIds =
                item.meetingMinuteContent.linkedBcrIds.filter(
                  (id) => String(id) !== String(bcrId)
                );
            }
          };

          forEachStoredSearchItem(state, patchItem);
        },
      // removeLinkedBcrFromMeetingMinuteByMeetingMinuteId: (
      //     state,
      //     action: PayloadAction<{
      //       meetingMinuteId: string;
      //       bcrId: string;
      //     }>
      //   ) => {
      //     const { meetingMinuteId, bcrId } = action.payload;

      //     const patchItem = (item: FileOrFolder) => {
      //       if (item.meetingMinuteContent?._id?.toString() !== meetingMinuteId) return;

      //       if (!Array.isArray(item.meetingMinuteContent.linkedBcrs)) {
      //         item.meetingMinuteContent.linkedBcrs = [];
      //         return;
      //       }

      //       item.meetingMinuteContent.linkedBcrs =
      //         item.meetingMinuteContent.linkedBcrs.filter(
      //           (linked) => linked?._id?.toString() !== bcrId
      //         );
      //     };

      //     state.visibleItems.forEach(patchItem);
      //     state.allItems.forEach(patchItem);
      //   },
        // New MM function - puts the meetingMinute to all bcrFiles.bcrContent.meetingMinute
        // (That we're used in updating the MM)
      syncMeetingMinuteToLinkedBcrFiles: (
          state,
          action: PayloadAction<{
            meetingMinuteFile: FileOrFolder;
          }>
        ) => {
          const { meetingMinuteFile } = action.payload;

          const meetingMinute = meetingMinuteFile.meetingMinuteContent;
          const meetingMinuteId = meetingMinute?._id?.toString();

          if (!meetingMinute || !meetingMinuteId) return;

          const linkedIds = new Set([
            ...(meetingMinute.linkedBcrs || [])
              .map((bcr) => bcr?._id?.toString())
              .filter((id): id is string => Boolean(id)),
            ...(meetingMinute.linkedBcrIds || [])
              .map(id => String(id))
              .filter(Boolean),
          ]);

          const patchItem = (item: FileOrFolder) => {
            const itemBcrId = item.bcrContent?._id?.toString();
            if (!itemBcrId || !item.bcrContent) return;

            // If this BCR is now linked to this meeting minute, set it
            if (linkedIds.has(itemBcrId)) {
              item.bcrContent.meetingMinute = meetingMinute;
              return;
            }

            // If this BCR used to point to this meeting minute but is no longer linked, clear it
            const currentMeetingMinuteId =
              typeof item.bcrContent.meetingMinute === "object"
                ? item.bcrContent.meetingMinute?._id?.toString()
                : item.bcrContent.meetingMinute?.toString();

            if (currentMeetingMinuteId === meetingMinuteId) {
              item.bcrContent.meetingMinute = null;
            }
          };

          forEachStoredSearchItem(state, patchItem);
        },
      clearDeletedMeetingMinuteFromBcrFiles: (
          state,
          action: PayloadAction<{ meetingMinuteId: string }>
        ) => {
          const { meetingMinuteId } = action.payload;

          const patchItem = (item: FileOrFolder) => {
            if (!item.bcrContent) return;

            const currentMeetingMinuteId =
              typeof item.bcrContent.meetingMinute === "object" &&
              item.bcrContent.meetingMinute !== null
                ? String(item.bcrContent.meetingMinute._id || "")
                : String(item.bcrContent.meetingMinute || "");

            if (currentMeetingMinuteId === String(meetingMinuteId)) {
              item.bcrContent.meetingMinute = null;
            }
          };

          forEachStoredSearchItem(state, patchItem);
      },
      // Overwrite BCRs with the new meeting minute created (if applied)
      setMeetingMinuteOnBcrFilesByIds: (
        state,
        action: PayloadAction<{
          bcrIds: string[];
          meetingMinute: IMeetingMinutes;
        }>
      ) => {
        const { bcrIds, meetingMinute } = action.payload;
        const bcrIdSet = new Set(bcrIds.map(String));

        const patchItem = (item: FileOrFolder) => {
          const itemBcrId = item.bcrContent?._id
            ? String(item.bcrContent._id)
            : null;

          if (!itemBcrId || !item.bcrContent) return;

          if (bcrIdSet.has(itemBcrId)) {
            item.bcrContent.meetingMinute = meetingMinute;
          }
        };

        forEachStoredSearchItem(state, patchItem);
      },
      // payload: { files: FileOrFolder[] } where each file has an updated bcrContent with .bcrRelations.referencedBy attached
      updateBcrContentsByBcrId: (state, action: PayloadAction<{ files: FileOrFolder[] }>) => {
        const byBcrId = new Map<string, any>();
        for (const f of action.payload.files) {
          const id = (f as any)?.bcrContent?._id?.toString?.();
          if (id) byBcrId.set(id, (f as any).bcrContent);
        }
        if (byBcrId.size === 0) return;

        const patchArray = (arr: FileOrFolder[]) => {
          for (let i = 0; i < arr.length; i++) {
            const item = arr[i] as any;
            const bcrId = item?.bcrContent?._id?.toString?.();
            if (!bcrId) continue;
            const updatedBcr = byBcrId.get(bcrId);
            if (updatedBcr) {
              // preserve the file but replace its bcrContent wholesale
              arr[i] = { ...item, bcrContent: updatedBcr };
            }
          }
        };
        patchArray(state.visibleItems);
        patchArray(state.allItems);
        if (state.searchEagerResult) patchArray(state.searchEagerResult.items);
        for (const page of Object.values(state.searchPageCache)) patchArray(page.items);
      },
    },
    extraReducers: (builder) => {
        builder
            // fetchItems pending, fulfilled, rejected
            .addCase(fetchItems.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.mode = 'explorer';
            })
            .addCase(fetchItems.fulfilled, (state, action: PayloadAction<FileOrFolder[]>) => {
                state.loading = false;
                state.allItems = action.payload;
                state.mode = 'explorer';
                console.log('response.data.items', action.payload)
                console.log("HEY MAN")
                state.visibleItems = action.payload.filter(item => item.parentId === state.currentFolderId);
            })
            .addCase(fetchItems.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // NO PENDING? NO REJECTED?
            .addCase(fetchAllItems.fulfilled, (state, action: PayloadAction<FileOrFolder[]>) => {
                state.loading = false;
                state.allItems = action.payload;
                console.log("HEY MAN 1")
            })

            .addCase(fetchItemsDocuSign.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchItemsDocuSign.fulfilled, (state, action: PayloadAction<FileOrFolder[]>) => {
                state.loading = false;
                state.allItems = action.payload;
                console.log("HEY MAN 2")
                // state.mode = 'explorer';
                state.visibleItems = action.payload.filter(item => item.parentId === state.currentFolderId);
            })
            .addCase(fetchItemsDocuSign.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Fetching searched items
            .addCase(fetchItemsSearchEnhanced.pending, (state, action) => {
                const nextRequest = typeof action.meta.arg === 'string' ? null : action.meta.arg;
                if (nextRequest?.prefetch) return;
                const canKeepCurrentPage = !!nextRequest && !!state.searchPage
                    && state.searchPageRequest?.query === nextRequest.query;
                if (!canKeepCurrentPage) {
                    state.visibleItems = [];
                    state.searchPage = null;
                    state.searchPageCache = {};
                    state.searchEagerResult = null;
                }
                state.searchRequestId = action.meta.requestId;
                state.searchPageRequest = nextRequest;
                state.loading = true;
                state.error = null;
                state.mode = 'search';
            })
            .addCase(fetchItemsSearchEnhanced.fulfilled, (state, action) => {
                if (typeof action.meta.arg !== 'string' && action.payload.pagination
                    && action.payload.deliveryMode !== 'eager') {
                    const request = { ...action.meta.arg, prefetch: false };
                    state.searchPageCache[searchPageQuery(request)] = {
                        items: action.payload.items,
                        pagination: action.payload.pagination,
                    };
                    const cachedKeys = Object.keys(state.searchPageCache);
                    if (cachedKeys.length > 40) delete state.searchPageCache[cachedKeys[0]];
                }
                if (typeof action.meta.arg !== 'string' && action.meta.arg.prefetch) return;
                if (state.searchRequestId !== action.meta.requestId) return;
                state.loading = false;
                if (typeof action.meta.arg !== 'string' && action.payload.pagination
                    && action.payload.deliveryMode === 'eager' && action.payload.eagerItems) {
                    const request = { ...action.meta.arg, prefetch: false };
                    state.searchEagerResult = {
                        key: eagerSearchKey(request),
                        items: action.payload.eagerItems,
                        pagination: action.payload.pagination,
                    };
                    state.searchPageCache = {};
                    const selected = selectEagerSearchPage(
                        action.payload.eagerItems,
                        request,
                        action.payload.pagination,
                    );
                    state.visibleItems = selected.items;
                    state.searchPage = selected.pagination;
                } else {
                    state.searchEagerResult = null;
                    state.visibleItems = action.payload.items;
                    state.searchPage = action.payload.pagination ?? null;
                }
            })
            .addCase(fetchItemsSearchEnhanced.rejected, (state, action) => {
                if (typeof action.meta.arg !== 'string' && action.meta.arg.prefetch) return;
                if (state.searchRequestId !== action.meta.requestId) return;
                state.loading = false;
                state.error = action.meta.aborted ? null : String(action.payload || 'Unable to load results');
            })
            .addCase((fetchItemsSearch.pending), (state, action) => {
                state.searchRequestId = action.meta.requestId;
                state.searchPageRequest = null;
                state.searchPage = null;
                state.searchPageCache = {};
                state.searchEagerResult = null;
                state.loading = true;
                state.error = null;
                state.mode = 'search';
            })
            .addCase((fetchItemsSearch.fulfilled), (state, action) => {
                if (state.searchRequestId !== action.meta.requestId) return;
                state.loading = false;
                state.visibleItems = action.payload;
                state.mode = 'search';
            })
            .addCase((fetchItemsSearch.rejected), (state, action) => {
                if (state.searchRequestId !== action.meta.requestId) return;
                state.loading = false;
                state.error = action.payload as string;
            })
            // Check for cutting! Moves items
            .addCase(moveItemsDirectory.fulfilled, (state) => {
                if (state.clipboardType === "cut") {
                    state.clipboard = []; //Clears after successful move
                    state.clipboardType = null;
                }
            })
            .addCase(moveItemsDirectory.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            //Paste
            .addCase(pasteItems.fulfilled, (state) => {
                // state.clipboard = []; //Clear clipboard after pasting
            })
            //All Metadata
            .addCase(fetchAllMetadata.pending, (state) => {
                // state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllMetadata.fulfilled, (state, action: PayloadAction<string[]>) => {
                // state.loading = false;
                state.metadata = action.payload;
            })
            .addCase(fetchAllMetadata.rejected, (state, action) => {
                // state.loading = false;
                state.error = action.payload as string;
            })
    }
})

export const moveItemsDirectory = createAsyncThunk(
    "fileExplorer/moveItemsDirectory",
    async (
      {
        items,
        newParentId,
        x,
        y,
      }: {
        items: FileOrFolder[];
        newParentId: string | null;
        x?: number;
        y?: number;
      },
      { dispatch, getState, rejectWithValue }
    ) => {
      try {
        const state = getState() as { fileExplorerReducer: FileExplorerState };
        const allItems = state.fileExplorerReducer.allItems;
  
        // 🔐 Step 1: Filter out "Untouchable" items
        const nonUntouchableItems = items.filter(
          (item) => item.accessLevel !== "Untouchable" 
          && item.accessLevel !== "ImmutableShell"
          && item.accessLevel !== "Sheltered" // Turn off? Supporting docs
        );
  
        const skippedUntouchables = items.length - nonUntouchableItems.length;
        if (skippedUntouchables > 0) {
          toast.error(
            `${skippedUntouchables} item${skippedUntouchables > 1 ? "s" : ""} cannot be moved due to access restrictions.`
          );
        }
        // 🧠 Step 2: Filter out items that can't be moved into themselves or their descendants
        const { validItems: movableItems, invalidItems } = filterValidMoveItems({
          items: nonUntouchableItems,
          newParentId,
          allItems,
        });
  
        if (invalidItems.length > 0) {
          toast.error(
            `${invalidItems.length} item${invalidItems.length > 1 ? "s" : ""} cannot be moved into their own descendant.`
          );
        }
        // if (movableItems.length === 0) {
        //   toast.error("No valid items to move.");
        //   return rejectWithValue("All selected items were invalid.");
        // }
        //DONT move 'Sheltered' items outside BCR folders
        const folderMovedTo = allItems.find(item => item._id === newParentId);
        const isOutsideBCR = (
            folderMovedTo?.parentId === null ||
            folderMovedTo?.systemFolder === process.env.NEXT_PUBLIC_BCR_FOLDER ||
            folderMovedTo?.systemFolder === process.env.NEXT_PUBLIC_SUPPORTING_DOC_FOLDER ||
            folderMovedTo?.systemFolder === process.env.NEXT_PUBLIC_MEETING_MINUTES_FOLDER ||
            folderMovedTo === undefined
        );
        const shelteredItems = movableItems.filter(item => item.accessLevel === "Sheltered");
        if (isOutsideBCR && shelteredItems.length > 0) {
            toast.error(`Cannot move supporting documents outside of BCR folders.`);
            // toast.error(`Cannot move ${shelteredItems.length} 'Sheltered' item${shelteredItems.length > 1 ? 's' : ''} outside of BCR folders.`);
            return rejectWithValue("Sheltered items cannot be moved outside of BCR folders.");
        }

        dispatch(setSaving(true));
  
        const existingItemsInDir = allItems.filter(
          (item) => item.parentId === newParentId
        );
  
        const updatedItems = resolveNewPositions(
          movableItems,
          existingItemsInDir,
          newParentId
        );
  
        const finalItems = updatedItems.map((item) => ({
          ...item,
          parentId: newParentId,
        }));
  
        dispatch(updateItemDirectory({ items: finalItems, newParentId, x, y }));
  
        // 💽 Step 4: Save updated state to DB
        await new Promise((resolve) => setTimeout(resolve, 0)); // simulate async
  
        const latestState = getState() as {
          fileExplorerReducer: FileExplorerState;
        };

        const client = getApiClient();
  
        await client.post("/api/files-folders/save-all", {
          allItems: latestState.fileExplorerReducer.allItems,
        });
  
        return { items: movableItems, newParentId };
      } catch (error: unknown) {
        if (
          axios.isAxiosError(error) &&
          error.response?.data?.error === "GRID_FULL"
        ) {
          toast.error(error.response?.data?.message);
          return rejectWithValue("Not enough space in the destination folder.");
        }
  
        console.error("Move item failed.", error);
        toast.error("Failed to move folder.");
        return rejectWithValue("Failed to move folder.");
      } finally {
        dispatch(setSaving(false));
      }
    }
  );

export const fetchItems = createAsyncThunk(
    "fileExplorer/fetchItems",
    async (_, { getState, rejectWithValue }) => {
        const { fileExplorerReducer } = getState() as { fileExplorerReducer: FileExplorerState };
        const parentId = fileExplorerReducer.currentFolderId; //Using redux state

        // Choose API client based on current route
        // const isSupportPage =
        // typeof window !== "undefined" &&
        // window.location.pathname.startsWith("/file-explorer");
        // const client = isSupportPage ? supportApi : api;
        const client = getApiClient();

        console.log("YES 22")


        try {
            const response = await client.get(`/api/files-folders`);
            return response.data.items;
        } catch (err) {
            const error = err as AxiosError<{ message: string }>;
            return rejectWithValue(error.response?.data?.message || "Error fetching items");
        }
    }
)


// NO VISIBLE SETTING
export const fetchAllItems = createAsyncThunk(
    "fileExplorer/fetchAllItems",
    async (_, { getState, rejectWithValue }) => {
        // const { fileExplorerReducer } = getState() as { fileExplorerReducer: FileExplorerState };
        const client = getApiClient();

        console.log("NO 22")
        try {
            const response = await client.get(`/api/files-folders`);
            return response.data.items;
        } catch (err) {
            const error = err as AxiosError<{ message: string }>;
            return rejectWithValue(error.response?.data?.message || "Error fetching items");
        }
    }
)

export const fetchItemsDocuSign = createAsyncThunk(
    "fileExplorer/fetchItemsDocuSign",
    async (_, { getState, rejectWithValue }) => {
        const { fileExplorerReducer } = getState() as { fileExplorerReducer: FileExplorerState };
        const parentId = fileExplorerReducer.currentFolderId; //Using redux state

        const client = getApiClient();

        try {
            const response = await client.get(`/api/files-folders`);
            return response.data.items;
        } catch (err) {
            const error = err as AxiosError<{ message: string }>;
            return rejectWithValue(error.response?.data?.message || "Error fetching items");
        }
    }
)

// export const fetchItemsByParent = createAsyncThunk(
//     "fileExplorer/fetchItemsByParent",
//     async (_, { getState, rejectWithValue }) => {
//         const { fileExplorerReducer } = getState() as { fileExplorerReducer: FileExplorerState };
//         const parentId = fileExplorerReducer.currentFolderId; //Using redux state

//         try {
//             const response = await api.get(`/api/files-folders?parentId=${parentId || ''}`);
//             return response.data.items;
//         } catch (err) {
//             const error = err as AxiosError<{ message: string }>;
//             return rejectWithValue(error.response?.data?.message || "Error fetching items");
//         }
//     }
// )

export const fetchItemsSearch = createAsyncThunk(
    "fileExplorer/fetchItemsSearch",
    async (search: string, { getState, rejectWithValue }) => {
        try {
            const client = getApiClient();

            const response = await client.get(`/api/files-folders/search/simple?search=${search}`);
            return response.data.items;
        } catch (err) {
            const error = err as AxiosError<{ message: string }>;
            return rejectWithValue(error.response?.data?.message || "Error fetching items");
        }
    }
)

export const fetchItemsSearchEnhanced = createAsyncThunk(
    "fileExplorer/fetchItemsSearchEnhanced",
    async (search: string | SearchPageRequest, { signal, rejectWithValue }) => {
        try {
            const client = getApiClient();
            
            const query = typeof search === 'string' ? search : searchPageQuery(search);
            const response = await client.get<SearchPageResponse>(`/api/files-folders/search/enhanced-3?${query}`, { signal });
            if (typeof search !== 'string' && !response.data.pagination) {
                return rejectWithValue('The server pagination update is required.');
            }
            // const response = await client.get(`/api/files-folders/search/enhanced?${search}`);
            return response.data;
        } catch (err) {
            const error = err as AxiosError<{ message: string }>;
            return rejectWithValue(error.response?.data?.message || "Error fetching items");
        }
    }
)

export const fetchItemsAdminSigningPage = createAsyncThunk(
    "fileExplorer/fetchItemsSearch",
    // async (search: string, { getState, rejectWithValue }) => {
    async (_, { getState, rejectWithValue }) => {
        try {
            // const response = await api.get(`/api/files-folders/search/enhanced?${search}`);
            const response = await api.get(`/api/files-folders/fetch-all-bcrs`);
            console.log('Enhance Searched Items!', response.data.items)
            return response.data.items;
        } catch (err) {
            const error = err as AxiosError<{ message: string }>;
            return rejectWithValue(error.response?.data?.message || "Error fetching items");
        }
    }
)

export const fetchBCRsNotSigned = createAsyncThunk(
        "fileExplorer/fetchItemsSearch",
        async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/bcrs-file/search-not-signed`);
            console.log('response.data.items' , response.data.items);
            return response.data.items;
        } catch (err) {
            const error = err as AxiosError<{ message: string }>;
            return rejectWithValue(error.response?.data?.message || "Error fetching bcrs not signed");
        }
    }
)

export const fetchArchivedBCRsMMs = createAsyncThunk(
        "fileExplorer/fetchItemsSearch",
        async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/files/search-archived`);
            console.log('response.data.items' , response.data.items);
            return response.data.items;
        } catch (err) {
            const error = err as AxiosError<{ message: string }>;
            return rejectWithValue(error.response?.data?.message || "Error fetching bcrs not signed");
        }
    }
)

export const fetchArchivedDraftBCRs = createAsyncThunk(
        "fileExplorer/fetchItemsSearch",
        async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/bcrs-file/search-draft-archived`);
            console.log('response.data.items' , response.data.items);
            return response.data.items;
        } catch (err) {
            const error = err as AxiosError<{ message: string }>;
            return rejectWithValue(error.response?.data?.message || "Error fetching bcrs not signed");
        }
    }
)

export const pasteItems = createAsyncThunk(
    "fileExplorer/pasteItems",
    async ({ x: startX, y: startY, newParentId }: { x: number; y: number; newParentId: string | null }, { getState, dispatch }) => {
        const getLatestState = getState as () => { fileExplorerReducer: FileExplorerState };
        const state = getLatestState().fileExplorerReducer; // Always get the latest state
        try {
            const parentIds = state.clipboard.map(item => ({
                _id: item._id,
                name: item.name
            }));

            // Basically support page only //file-explorer

            dispatch(setSaving(true));
            // ✅ Step 1: Duplicate Items
            const response = await api.post(`/api/files-folders/duplicate-children`, { parentIds, newParentId, startX, startY });

            // ✅ Step 2: Extract moved items
            const allItems: FileOrFolder[] = response.data.allItems;
            const rootItems: FileOrFolder[] = response.data.rootItems;

            // ✅ Step 3: Resolve Overlaps
            const resolveResponse = await api.post(`/api/files-folders/resolve-overlaps-linear`, {
                parentId: newParentId,
                movedItems: rootItems
            });

            const updatedItems: FileOrFolder[] = resolveResponse.data.items;

            const updatedMap = new Map(updatedItems.map(item => [item._id, item]));
            const mergedItems = allItems.map(item => updatedMap.get(item._id) || item);

            // ✅ Step 4: Update Redux Directly
            dispatch(updateItemsAfterPaste({ mergedItems, newParentId }));

            // ✅ Step 5: Show Deletion Warning (if applicable)
            if (response.data.deletedItems?.length) {
                const deletedNames = response.data.deletedItems.join(", ");
                toast.error(`Some items were deleted before pasting: ${deletedNames}`);
            }

            dispatch(setSaving(false));
            return;
        } catch (error: unknown) {
            if (axios.isAxiosError(error)) {
                if (error.response?.data?.error === "GRID_FULL") {
                    toast.error("No space available! Delete some items first.");
                } else {
                    toast.error(error.response?.data?.message);
                }
            } else {
                console.error("Unexpected error:", error);
                toast.error("An unexpected error occurred.");
            }
            dispatch(setSaving(false));
        }
    }
);

export const fetchAllMetadata = createAsyncThunk(
    "fileExplorer/fetchAllMetadata",
    async (_, { rejectWithValue }) => {
        try {
            const client = getApiClient();
            const response = await client.get(`/api/files-folders/get-all-metadata`);
            return response.data.metadata;
        } catch (err) {
            const error = err as AxiosError<{ message: string }>;
            return rejectWithValue(error.response?.data?.message || "Error fetching metadata");
        }
    }
)

// Files too large!
// export const saveMovedItems = createAsyncThunk(
//     "fileExplorer/saveMovedItems",
//     async (updatedItems: FileOrFolder[]) => {
//         try {
//             await api.post("/api/files-folders/move-items", { visibleItems: updatedItems });
//             console.log("✅ Moved items saved successfully");
//         } catch (error) {
//             console.error("❌ Error saving moved items:", error);
//         }
//     }
// );

export const saveMovedItems = createAsyncThunk(
  "fileExplorer/saveMovedItems",
  async (updatedItems: FileOrFolder[]) => {
    try {
      const slimItems = updatedItems.map((item) => ({
        _id: item._id,
        type: item.type,
        itemCategory: item.itemCategory ?? (item.type === "folder" ? "folder" : "file"),
        parentId: item.parentId ?? null,
        x: item.x,
        y: item.y,
      }));

      const client = getApiClient();

      await client.post("/api/files-folders/move-items", { items: slimItems });
      console.log("✅ Moved items saved successfully");
    } catch (error) {
      console.error("❌ Error saving moved items:", error);
      throw error;
    }
  }
);

// export const saveMovedItems = createAsyncThunk(
//   "fileExplorer/saveMovedItems",
//   async (updatedItems: FileOrFolder[]) => {
//     try {
//       // 🔹 Only send what the server actually needs
//       const slimItems = updatedItems.map((item) => ({
//         _id: item._id,
//         type: item.type,          // "file" | "folder"
//         parentId: item.parentId ?? null,
//         x: item.x,
//         y: item.y,
//       }));

//       // // Choose API client based on current route // Inside utils/getApiClient()
//       // const isSupportPage =
//       // typeof window !== "undefined" &&
//       // window.location.pathname.startsWith("/file-explorer");
//       // const client = isSupportPage ? supportApi : api;
//       const client = getApiClient();

//       await api.post("/api/files-folders/move-items", { items: slimItems });
//       console.log("✅ Moved items saved successfully");
//     } catch (error) {
//       console.error("❌ Error saving moved items:", error);
//       throw error;
//     }
//   }
// );

// Used in dispatchers above
const buildBreadcrumb = (
  folderId: string | null,
  allItems: FileOrFolder[]
): { _id: string | null; name: string }[] => {
  const breadcrumb: { _id: string | null; name: string }[] = [];

  if (!folderId) {
    return [{ _id: null, name: "Files (Root)" }];
  }

  let cur = allItems.find((i) => i._id === folderId);

  while (cur) {
    breadcrumb.unshift({
      _id: cur._id ?? null,
      name: cur.name,
    });

    cur = cur.parentId
      ? allItems.find((i) => i._id === cur!.parentId)
      : undefined;
  }

  breadcrumb.unshift({ _id: null, name: "Files (Root)" });

  return breadcrumb;
};

export const { 
    updateItemsAfterPaste,
    setSaving,
    getVisibleItems,
    setVisibleItemsMeetingMinutes,
    updateItemPositions,
    resolveOverlapsInState,
    updateItemDirectory,
    setMode,
    setCurrentFolderId, 
    setCurrentFolderNull,
    setCurrentFolderId2, 
    navigateToFolder, 
    setPropertiesPath,
    navigateToFolderProperties,
    setSelectedItems, 
    toggleSelectedItem,
    addSelectedItem,
    removeSelectedItem,
    clearSelectedItems,
    copyItems,
    cutItems,
    setDropZonePos,
    clearDropZonePos,
    addItem,
    deleteItem,
    updateItem,
    updateItemSearched,
    renameFolder,
    updateSupportingDocBCRName,
    updateSupportingDocMMName,
    updateVisibleItemName,
    addItemDashboard,
    setSignedBCRVisibleItems,
    removeSignedBCRVisibleItems,
    updateBCRDashboard,
    clearItemsDashboard,
    invalidateSearchResultsCache,
    showCachedSearchPage,
    updateBCRSupportingDocsDashboard,
    replaceBCRSupportingDocsDashboard,
    updateDSImageAndParticipantSignatureStatus,
    nullifyParticipantSignatureStatusAndDSImage,
    updateParticipantExtractedSignature,
    setBCRMMFile_FileExplorer,
    removeFileFromVisibleItems,
    addFileToVisibleItems,
    updateBcrContentsByBcrId,
    // MM Stuff
    clearMeetingMinuteFromBCRByBcrId,
    appendBcrLinkToMeetingMinuteByMeetingMinuteId,
    removeLinkedBcrFromMeetingMinuteByMeetingMinuteId,
    syncMeetingMinuteToLinkedBcrFiles,
    clearDeletedMeetingMinuteFromBcrFiles,
    setMeetingMinuteOnBcrFilesByIds,
    // NEW
    goToFolder,
    goBack,
    goForward,
    goToPage,
    setCurrentPage,
    clampCurrentPage, // ?
    resetExplorerNavigation
    } = fileExplorerSlice.actions;

export default fileExplorerSlice.reducer;


export const resolveOverlaps = (items: FileOrFolder[]) => {
  // Work on a copy if you want to keep it pure
  const sorted = [...items].sort((a, b) => (a.y - b.y) || (a.x - b.x));

  const occupied = new Set<string>();

  for (const item of sorted) {
    let x = Math.max(0, Math.min(MAX_COLUMNS - 1, item.x));
    let y = Math.max(0, Math.min(MAX_ROWS - 1, item.y));

    let safety = 0;
    while (occupied.has(`${x},${y}`)) {
      x++;
      if (x >= MAX_COLUMNS) {
        x = 0;
        y++;
      }
      if (y >= MAX_ROWS) {
        console.warn("⚠ GRID FULL for item:", item.name);
        break; // or clamp to last cell
      }
      safety++;
      if (safety > MAX_COLUMNS * MAX_ROWS) break;
    }

    item.x = x;
    item.y = y;
    occupied.add(`${x},${y}`);
  }

  return items;
};

const resolveNewPositions = (
    items: FileOrFolder[], 
    existingItems: FileOrFolder[],
    newParentId: string | null,
    x?: number, 
    y?: number
): FileOrFolder[] => {
    const occupiedPositions = new Set(existingItems.map(item => `${item.x},${item.y}`));

    let nextX = x ?? 0;
    let nextY = y ?? 0;

    return items.map(item => {
        while (occupiedPositions.has(`${nextX},${nextY}`)) {
            nextX++;
            if (nextX >= MAX_COLUMNS) {
                nextX = 0;
                nextY++;
            }
            if (nextY >= MAX_ROWS) {
                console.warn("⚠ No space available to move item:", item.name);
                return item; // Skip item if no space
            }
        }

        const updatedItem = { ...item, x: nextX, y: nextY };
        occupiedPositions.add(`${nextX},${nextY}`);
        nextX++; // Move to the next available spot

        return updatedItem;
    });
};

function findFolderById(folderId: string, itemsList: FileOrFolder[]): FileOrFolder | undefined {
    for (const item of itemsList) {
        if (item._id === folderId) {
            return item; // Found in the main list
        }

        // If item has children, search inside them recursively
        if (item.children && item.children.length > 0) {
            const foundInChildren = findFolderById(folderId, item.children);
            if (foundInChildren) return foundInChildren; // Return if found
        }
    }

    return undefined; // Not found
}
